import { useState } from 'react'
import { redirect, useLoaderData, useNavigate } from 'react-router'
import { Flex } from '@radix-ui/themes'
import { PageHeading } from '@/components/subscription/page-heading'
import { Button } from '@/components/subscription/button'
import { TextLink } from '@/components/subscription/text-link'
import { ScheduleSelector } from './schedule-selector'
import { formatDate } from '@/lib/shipment'
import { findShippingStatus } from '@/lib/order'
import { createClient, JSONAPIResponse } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { SubscriptionSchedules } from '@/api-client/SubscriptionSchedules'
import { Subscription, SubscriptionInventory } from '@/api-client/data-contracts'
import * as styles from './styles.css'

export const loader = async () => {
  const subscriptions = (await fetchSubscriptions()) as unknown as JSONAPIResponse<typeof fetchSubscriptions>
  const subscription = subscriptions.data.data[0]

  if (subscription) {
    const address = (await fetchAddress(subscription)) as unknown as JSONAPIResponse<typeof fetchAddress>
    const schedules = await fetchSchedules(address.data.data.attributes.postal_code)
    const placements = await fetchPlacements(subscription)
    const orders = await fetchOrders(subscription)

    return [subscriptions, schedules, placements, orders]
  } else {
    return redirect('/subscription/mypage')
  }
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions()
}

function fetchAddress(subscription: Subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(subscription.id)
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, upcoming: '1', limit: 9 })
}

function fetchPlacements(subscription: Subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdPlacements(subscription.id)
}

function fetchOrders(subscription: Subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdOrders(subscription.id, { include: ['order_items.shipping_statuses'] })
}

function fetchPlannedSchedules(subscription: Subscription, perMonth: number, weekday: number) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdSchedulesPlan(subscription.id, {
    per_month: perMonth.toString(),
    weekday: weekday.toString(),
  })
}

export const SubscriptionMypageSchedule: React.FC = () => {
  const [
    {
      data: { data: subscriptions, included: subscriptionsIncluded },
    },
    {
      data: { data: schedules, included: schedulesIncluded },
    },
    {
      data: { data: placements },
    },
    {
      data: { data: orders, included: ordersIncluded },
    },
  ] = useLoaderData() as [
    JSONAPIResponse<typeof fetchSubscriptions>,
    JSONAPIResponse<typeof fetchSchedules>,
    JSONAPIResponse<typeof fetchPlacements>,
    JSONAPIResponse<typeof fetchOrders>,
  ]
  const navigate = useNavigate()
  const subscription = subscriptions[0]
  const inventories = schedulesIncluded.filter(
    (item) => item.type === 'subscription_inventory' && !item.attributes.is_soldout,
  ) as unknown as SubscriptionInventory[]
  const [futureSchedules, setFutureSchedules] = useState(() => {
    return subscription.relationships.inventories.data
      .map((inventory: SubscriptionInventory) => schedules.find((s) => s.relationships.inventory.data.id === inventory.id))
      .sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0])
  })
  const [perMonth, setPerMonth] = useState(subscriptionsIncluded.filter((item) => item.type === 'subscription_inventory').length)
  const [weekday, setWeekday] = useState(() => {
    const schedule = schedules.find((s) => subscriptionsIncluded.some((item) => s.relationships.inventory.data.id === item.id))
    const deliverAtDay = new Date(schedule?.attributes.deliver_at[0] * 1000).getDay()

    return deliverAtDay === 2 || deliverAtDay === 3 ? 2 : 5
  })
  const latestShipping = findShippingStatus(orders[0], ordersIncluded)
  const latestShippingStatus = latestShipping?.attributes?.status

  let options = []
  if (inventories[0]) options.push([inventories[0]])
  if (inventories.length > 2) options.push([inventories[0], inventories[2]])
  if (inventories.length > 3) options.push(inventories)

  const handleChange = async ({ perMonth, weekday }: { perMonth: number; weekday: number }) => {
    setPerMonth(perMonth)
    setWeekday(weekday)

    const planned = (await fetchPlannedSchedules(subscription, perMonth, weekday)) as unknown as JSONAPIResponse<typeof fetchPlannedSchedules>

    setFutureSchedules(planned.data.data)
  }

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async () => {
    try {
      await createClient(Subscriptions).putSubscriptionsSubscriptionIdContract(subscription.id, { per_month: perMonth.toString(), weekday: weekday.toString() })
      await navigate('/subscription/mypage', { state: { message: 'お届けスケジュールを変更しました' } })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex direction="column" align="center" gap="32px">
      <PageHeading>受け取りスケジュール変更</PageHeading>
      <Flex direction="column" align="center" gap="24px" width="100%">
        <ScheduleSelector schedules={schedules} perMonth={perMonth} weekday={weekday} onChange={handleChange} />
        <Flex direction="column" align="start" gap="8px" className={styles.card}>
          <h3 className={styles.cardHeading}>変更後のお届け予定</h3>
          <Flex direction="column" align="start" gap="8px">
            {['not_shipped', 'preparation', 'transit'].includes(latestShippingStatus) && (
              <Flex direction="row" align="center" gap="4px" className={styles.scheduledDate}>
                <p>{formatDate(1000 * latestShipping.attributes.deliver_at, 'M/d（E）')}</p>
                <span className={styles.statusLabel}>{latestShipping.attributes.status_label}</span>
              </Flex>
            )}
            {futureSchedules.map((schedule) => (
              <span key={schedule.id} className={styles.scheduledDate}>
                {formatDate(schedule.attributes.deliver_at[0] * 1000, 'M/d（E）')}
              </span>
            ))}
          </Flex>
          {/* TODO: 次回のplacement.status === orderedでかつ曜日を変えようとしたときに表示。 */}
          {/* <p className={styles.warn}>※すでに次回分は出荷準備中のため、それ以降変更が適用されます</p> */}
        </Flex>
        <Flex direction="column" align="center" gap="16px" width="100%">
          <Button fluid as="button" onClick={handleSubmit}>
            保存
          </Button>
          <TextLink to="/subscription/mypage">マイページに戻る</TextLink>
        </Flex>
      </Flex>
    </Flex>
  )
}
